"use client";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shadcnComponents/ui/form";
import { Input } from "@/shadcnComponents/ui/input";
import { Textarea } from "@/shadcnComponents/ui/textarea";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shadcnComponents/ui/alert";
import { useInfluencer } from "@/Providers/Influencer";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogTrigger,
} from "@/shadcnComponents/ui/alert-dialog";
import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";

const formSchema = z.object({
  accountHolderName: z
    .string()
    .min(2, { message: "Name is required and must be at least 2 characters." }),
  ifscCode: z
    .string()
    .min(1, { message: "IFSC code is required." })
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, {
      message: "Invalid IFSC code. Format should be like SBIN0123456.",
    }),
  accountNumber: z
    .string()
    .min(1, { message: "Account number is required." })
    .min(9)
    .max(18)
    .regex(/^\d+$/, {
      message:
        "Account number must contain only digits and be between 9-18 characters.",
    }),
  panCardNumber: z
    .string()
    .min(1, { message: "PAN card number is required." })
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, {
      message: "Invalid PAN card number. Format should be like ABCDE1234F.",
    }),
  address: z.string().min(10, {
    message: "Address is required and must be at least 10 characters.",
  }),
});

export default function InfluencerBankDetailsForm() {
  const influencerState = useInfluencer();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountHolderName: "",
      ifscCode: "",
      accountNumber: "",
      panCardNumber: "",
      address: "",
    },
  });

  const { reset } = form;

  useEffect(() => {
    async function fetchBankDetails() {
      const result = await influencerState?.getLatestInfluencerBankDetails();
      if (result?.success && result?.latestInfluencerBankDetails) {
        reset(result.latestInfluencerBankDetails);
        const query = new URLSearchParams(window.location.search);
        if (query.has("edit", "true")) {
          setIsEditable(true);
        } else {
          setIsEditable(false);
        }
      } else {
        setIsEditable(true);
      }
    }
    fetchBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveInfluencerBankDetails = async (values) => {
    const result = await influencerState?.saveInfluencerBankDetails(values);
    if (result?.success) {
      setIsSubmitted(true);
      setIsDialogOpen(false);
    } else {
      toast({
        variant: "destructive",
        title: "Failed to save influencer bank details",
        description: result?.error || "An error occurred",
      });
    }
  };

  function onSubmit(values: z.infer<typeof formSchema>) {
    saveInfluencerBankDetails(values);
  }

  return (
    <>
      <MobileMenuBar />
      <div className="flex min-h-screen items-center justify-center bg-[#171717] p-4 dark font-public mb-10">
        <Card className="w-full max-w-lg border-neutral-800 bg-neutral-900 text-white animate-fadeIn">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Bank Details</CardTitle>
            <CardDescription className="text-neutral-400">
              Please provide your bank details for payment processing
            </CardDescription>
            <CardDescription className="text-neutral-400 mt-2">
              All fields marked with <span className="text-red-500">*</span> are
              required
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <Alert className="border-green-800 bg-green-950 text-green-400 animate-slideIn">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Success!</AlertTitle>
                <AlertDescription>
                  Your bank details have been submitted successfully.
                </AlertDescription>
              </Alert>
            ) : (
              <Form {...form}>
                <form className="space-y-6">
                  <FormField
                    control={form.control}
                    name="accountHolderName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          Account Holder's Name
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditable}
                            placeholder="Enter full name as per bank records"
                            {...field}
                            required
                            className="border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ifscCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          IFSC Code
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditable}
                            placeholder="e.g., SBIN0123456"
                            {...field}
                            required
                            className="border-neutral-700 bg-neutral-800 text-white  placeholder:text-neutral-500"
                            value={field.value.toUpperCase()}
                            onChange={(e) => {
                              field.onChange(e.target.value.toUpperCase());
                            }}
                          />
                        </FormControl>
                        <FormDescription className="text-neutral-500">
                          11-character code that identifies your bank branch
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          Account Number
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditable}
                            placeholder="Enter your account number"
                            {...field}
                            required
                            className="border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="panCardNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          PAN Card Number
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditable}
                            placeholder="e.g., ABCDE1234F"
                            {...field}
                            required
                            className="border-neutral-700 bg-neutral-800 text-white  placeholder:text-neutral-500"
                            value={field.value.toUpperCase()}
                            onChange={(e) => {
                              field.onChange(e.target.value.toUpperCase());
                            }}
                          />
                        </FormControl>
                        <FormDescription className="text-neutral-500">
                          Required for TDS deduction purposes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          Full Permanent Address
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            disabled={!isEditable}
                            placeholder="Enter your complete permanent address"
                            {...field}
                            required
                            className="min-h-[100px] border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-500"
                          />
                        </FormControl>
                        <FormDescription className="text-neutral-500">
                          Required for TDS deduction
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Alert className="border-amber-800 bg-amber-950 text-amber-400">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>TDS Deduction Notice</AlertTitle>
                    <AlertDescription className="text-white">
                      A standard 10% Tax Deduction at Source (TDS) will be
                      applied. However, this amount can be reclaimed when you
                      file your taxes.
                    </AlertDescription>
                  </Alert>

                  <AlertDialog
                    open={isDialogOpen}
                    onOpenChange={setIsDialogOpen}
                  >
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={!isEditable}
                        type="button"
                        className="w-full bg-red-600 text-white hover:bg-red-700"
                      >
                        Submit Bank Details
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Details cannot be edited for the next 90 days
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            form.handleSubmit(onSubmit)();
                          }}
                          type="submit"
                        >
                          Continue
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
